import React, { useMemo } from "react";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Textarea } from "../../@/components/ui/textarea";
import { Form, FormField } from "../components/ui/form";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "../../@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

export default function QuestionFormCard({
  questionForm,
  answers,
  appendAnswer,
  removeAnswer,
  questionType,
  correctAnswerIndex,
  onTypeChange,
  onCorrectAnswerChange,
  onSave,
  onCancel,
  isEditing,
  onOpenQuestionContentEditor,
  onOpenExplanationEditor, // currently unused (kept for compatibility)
  editCtaLabel,
  createCtaLabel,
  dialogContentClassName,
}) {
  const disableTypeSelect = Boolean(
    isEditing &&
    typeof questionForm?.getValues === "function" &&
    questionForm.getValues("isExisting"),
  );

  const hasCorrectAnswer = useMemo(() => {
    if (questionType === "multiple_choice") {
      return answers.some((a) => a.is_correct);
    }
    if (questionType === "true_false") {
      return answers.some((a) => a.is_correct);
    }
    if (questionType === "numeric") {
      return answers.length === 1 && !!answers[0]?.is_correct;
    }
    return true;
  }, [answers, questionType]);

  const primaryLabel = isEditing
    ? editCtaLabel || "Update Question"
    : createCtaLabel || "Create Question";

  const answersErrorMessage =
    questionForm?.formState?.errors?.answers?.message || null;

  return (
    <DialogContent
      className={
        dialogContentClassName ||
        "max-w-2xl w-[95vw] max-h-[90vh] flex flex-col p-0 bg-(--surface-panel) text-(--neutral-900) border border-(--neutral-300)"
      }
      onEscapeKeyDown={(e) => e.stopPropagation()}
      onPointerDownOutside={(e) => e.preventDefault()}
      onInteractOutside={(e) => e.preventDefault()}
    >
      <DialogHeader className="px-4 pt-4">
        <DialogTitle className="text-lg font-semibold text-(--neutral-900)">
          {isEditing ? "Edit Question" : "Add New Question"}
        </DialogTitle>
      </DialogHeader>

      <div className="flex-1 overflow-y-auto px-6 pb-4">
        <Form {...questionForm}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={questionForm.control}
                name="type"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Question Type *</FieldLabel>
                    <FieldContent>
                      <Select
                        value={field.value}
                        onValueChange={onTypeChange}
                        disabled={disableTypeSelect}
                      >
                        <SelectTrigger
                          disabled={disableTypeSelect}
                          className={cn(
                            "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) mt-1",
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                          )}
                        >
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900)">
                          <SelectItem
                            value="multiple_choice"
                            className="data-[highlighted]:bg-(--neutral-100)"
                          >
                            Multiple Choice
                          </SelectItem>
                          <SelectItem
                            value="true_false"
                            className="data-[highlighted]:bg-(--neutral-100)"
                          >
                            True/False
                          </SelectItem>
                          <SelectItem
                            value="numeric"
                            className="data-[highlighted]:bg-(--neutral-100)"
                          >
                            Numeric
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: "This is required" }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                )}
              />

              <FormField
                control={questionForm.control}
                name="score"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Points *</FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        type="number"
                        min="1"
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value) || 1)
                        }
                        className={cn(
                          "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) focus-visible:ring-0 focus:border-[var(--color-accent)] mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                        )}
                      />
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: "This is required" }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                )}
              />
            </div>

            <FormField
              control={questionForm.control}
              name="question_text"
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>Question *</FieldLabel>
                  <FieldContent>
                    <Textarea
                      {...field}
                      placeholder="Enter your question here"
                      rows={3}
                      className={cn(
                        "resize-none border border-(--neutral-300) bg-(--surface-panel) text-(--neutral-900) focus:border-[var(--color-accent)] focus:ring-0 mt-1",
                        fieldState.error &&
                          "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                      )}
                    />
                    <FieldError
                      errors={
                        fieldState.error
                          ? [{ message: "This is required" }]
                          : []
                      }
                    />
                  </FieldContent>
                </Field>
              )}
            />

            <FormField
              control={questionForm.control}
              name="question_content"
              render={({ field, fieldState }) => (
                <Field>
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <FieldLabel>
                      Content
                      <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <Button
                      type="button"
                      onClick={onOpenQuestionContentEditor}
                      size=""
                      variant="outline"
                      className=""
                    >
                      Edit in Fullscreen
                    </Button>
                  </div>
                  <FieldContent>
                    <Textarea
                      {...field}
                      placeholder="Enter additional content required for the question"
                      rows={3}
                      className={cn(
                        "resize-none border border-(--neutral-300) bg-(--surface-panel) text-(--neutral-900) focus:border-[var(--color-accent)] focus:ring-0 font-body mt-1",
                        fieldState.error &&
                          "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                      )}
                    />
                    <FieldError errors={[]} />
                  </FieldContent>
                </Field>
              )}
            />

            {questionType !== "description" && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <FieldLabel className="text-(--neutral-800)">
                    Answer Options (Choose the correct answer)
                  </FieldLabel>
                  {questionType === "multiple_choice" && (
                    <Button
                      type="button"
                      onClick={() =>
                        appendAnswer({ text: "", is_correct: false })
                      }
                      variant="outline"
                      size=""
                      className=""
                    >
                      Add Option
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  {questionType === "numeric" ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="correct_answer"
                        checked={correctAnswerIndex === 0}
                        onChange={() => onCorrectAnswerChange(0)}
                        className="h-4 w-4 accent-[var(--color-accent)]"
                      />
                      <FormField
                        control={questionForm.control}
                        name={`answers.0.text`}
                        render={({ field, fieldState }) => (
                          <Field className="flex-1 m-0 gap-0">
                            <FieldContent>
                              <Input
                                {...field}
                                placeholder="Enter numeric answer"
                                type="number"
                                onChange={(e) => {
                                  field.onChange(e);
                                  const currentAnswers =
                                    questionForm.getValues("answers") || [];
                                  if (currentAnswers.length === 1) {
                                    questionForm.setValue("answers", [
                                      {
                                        ...currentAnswers[0],
                                        is_correct: true,
                                      },
                                    ]);
                                  }
                                }}
                                className={cn(
                                  "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900)",
                                  fieldState.error &&
                                    "border-(--color-destructive)",
                                )}
                              />
                              <FieldError errors={[]} />
                            </FieldContent>
                          </Field>
                        )}
                      />
                    </div>
                  ) : (
                    answers.map((answer, index) => (
                      <div
                        key={answer.id || index}
                        className="flex items-center gap-2"
                      >
                        <input
                          type="radio"
                          name="correct_answer"
                          checked={correctAnswerIndex === index}
                          onChange={() => onCorrectAnswerChange(index)}
                          className="h-4 w-4 accent-[var(--color-accent)]"
                        />
                        <FormField
                          control={questionForm.control}
                          name={`answers.${index}.text`}
                          render={({ field, fieldState }) => (
                            <Field className="flex-1 m-0 gap-0">
                              <FieldContent>
                                <Input
                                  {...field}
                                  placeholder={
                                    questionType === "true_false"
                                      ? answer.text
                                      : questionType === "numeric"
                                        ? "Enter numeric answer"
                                        : "Enter Option Here"
                                  }
                                  readOnly={questionType === "true_false"}
                                  type={
                                    questionType === "numeric"
                                      ? "number"
                                      : "text"
                                  }
                                  className={cn(
                                    "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900)",
                                    fieldState.error &&
                                      "border-(--color-destructive)",
                                  )}
                                />
                                <FieldError errors={[]} />
                              </FieldContent>
                            </Field>
                          )}
                        />
                        {questionType !== "true_false" &&
                          answers.length > 2 && (
                            <Button
                              type="button"
                              onClick={() => removeAnswer(index)}
                              variant="ghost"
                              size="sm"
                              className="text-(--neutral-500) hover:text-(--color-destructive)"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                      </div>
                    ))
                  )}
                </div>

                {answersErrorMessage && (
                  <p className="text-xs text-(--color-destructive) mt-2">
                    {answersErrorMessage}
                  </p>
                )}
              </div>
            )}

            {!hasCorrectAnswer && (
              <p className="text-xs text-(--color-destructive) mt-1">
                Choose a correct answer to enable saving.
              </p>
            )}

            <FormField
              control={questionForm.control}
              name="explanation"
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>
                    Explanation
                    <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                      <Info className="w-3 h-3" />
                      Optional
                    </div>
                  </FieldLabel>
                  <FieldContent>
                    <Textarea
                      {...field}
                      placeholder="Provide an explanation for the correct answer"
                      rows={3}
                      className={cn(
                        "resize-none border border-(--neutral-300) bg-(--surface-panel) text-(--neutral-900) focus:border-[var(--color-accent)] focus:ring-0 font-body mt-1",
                        fieldState.error &&
                          "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                      )}
                    />
                    <FieldError errors={[]} />
                  </FieldContent>
                </Field>
              )}
            />
          </div>
        </Form>
      </div>

      <DialogFooter className="px-6 pb-6 flex justify-between gap-2">
        <DialogClose asChild>
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
            className=""
          >
            Cancel
          </Button>
        </DialogClose>
        <Button
          type="button"
          onClick={onSave}
          variant=""
          className=""
          disabled={!hasCorrectAnswer}
        >
          {primaryLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
